// src/services/agentOffline.js
//
// Recognising "the plant's agent is offline" as distinct from "the query failed".
//
// This distinction is the whole point of the offline banner: an agent that has
// gone quiet is an operational fact to state calmly over the last known data,
// not a red error that makes the dashboard look broken.
//
// The backend returns `{ error:'agent_offline', plantId, since, lastDataTs }`
// with a 503. That reaches the UI in several shapes depending on the caller, so
// everything below is written to accept all of them:
//
//   - an axios error thrown by a failed request  (err.response.data)
//   - a fetch Response body already parsed        (the plain object)
//   - a WebSocket subscription error, which arrives as just a message string

const OFFLINE_CODE = 'agent_offline';

// The message the server puts on the error it throws internally, which the
// subscription path forwards verbatim as a string.
const OFFLINE_MESSAGE = /^Agent for (.+) is offline$/;

/** Pull the offline payload out of whatever shape we were handed, or null. */
export function parseAgentOffline(input) {
  if (!input) return null;

  // A bare message string (the WebSocket subscription path).
  if (typeof input === 'string') {
    const m = input.match(OFFLINE_MESSAGE);
    return m ? { plantId: m[1], since: null, lastDataTs: null } : null;
  }

  // An axios error, or anything else carrying a response body.
  const body = input.response?.data ?? input.data ?? input;

  if (body && typeof body === 'object' && body.error === OFFLINE_CODE) {
    return {
      plantId: body.plantId ?? null,
      since: typeof body.since === 'number' ? body.since : null,
      lastDataTs: typeof body.lastDataTs === 'number' ? body.lastDataTs : null,
    };
  }

  // An Error whose message is the offline sentence (thrown through a layer that
  // kept only the message).
  if (typeof input.message === 'string') {
    const m = input.message.match(OFFLINE_MESSAGE);
    if (m) return { plantId: m[1], since: null, lastDataTs: null };
  }

  return null;
}

/** True when this response or error means the plant's agent is offline. */
export function isAgentOffline(input) {
  return parseAgentOffline(input) !== null;
}

export default { isAgentOffline, parseAgentOffline };
